/**
 * Redux slice for global UI state: sidebar, theme, notifications, loading overlay.
 *
 * @remarks
 * Theme preference is persisted to `localStorage` under the key `"theme"`.
 * Notifications are auto-assigned a unique `id` on dispatch.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'

/** Severity level for toast-style notifications. */
export type NotificationType = 'success' | 'error' | 'warning' | 'info'

/** A single queued notification for the NotificationSystem component. */
export interface Notification {
  id: string
  type: NotificationType
  message: string
  duration?: number // Auto-dismiss duration in ms
}

/** Redux state for global UI concerns. */
interface UIState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  notifications: Notification[]
  globalLoading: boolean // For full-page loading overlays
}

const initialState: UIState = {
  sidebarOpen: true,
  theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  notifications: [],
  globalLoading: false,
}

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload
      localStorage.setItem('theme', action.payload)
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const id = `notification-${Date.now()}-${Math.random()}`
      state.notifications.push({
        id,
        ...action.payload,
      })
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: state => {
      state.notifications = []
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload
    },
  },
})

// Actions
export const { toggleSidebar, setSidebarOpen, setTheme, addNotification, removeNotification, clearNotifications, setGlobalLoading } =
  uiSlice.actions

// Selectors
/** Select the full UI sub-state. */
export const selectUI = (state: RootState) => state.ui
/** Select whether the sidebar is open. */
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen
/** Select the current theme (`'light'` or `'dark'`). */
export const selectTheme = (state: RootState) => state.ui.theme
/** Select queued toast notifications. */
export const selectNotifications = (state: RootState) => state.ui.notifications
/** Select the global loading overlay flag. */
export const selectGlobalLoading = (state: RootState) => state.ui.globalLoading

// Reducer
export default uiSlice.reducer
